import { Button, Input, InputNumber, Select, Table, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import API from 'API';
import { CheckCircleFilled, CloseCircleFilled, FileAddFilled, MinusCircleFilled, PlusCircleFilled, SaveFilled, SaveOutlined } from '@ant-design/icons';
import { correct_media, downloadFile, formatScore } from 'general_function';
import toastr from 'toastr';
import { colorSet } from 'const';
import ShowCSVFile from 'components/general/ShowCSVFile';

const NOW = dayjs();

const mandatoryHeader = ["student_number", "score"]

function SubmitFile({ upFileRate, setUpFileRate, getData }) {
    return upFileRate !== null && <ShowCSVFile open={ upFileRate !== null }
        onCancel={() => setUpFileRate(null)}
        submitPath="/submit-custom-score-file/"
        addOnPayLoad={{ part_id: upFileRate.id }}
        mandatoryHeader={mandatoryHeader}
        onDone={() => {
            setUpFileRate(null);
            getData(true);
        }}
        title={upFileRate.title}
    />
}

function ColumnTitle({ item, index, setColumns, original, tests, allowEdit, setUpFileRate }) {
    return <div className='text-center' style={{
        width: 200, position: "relative"
    }}>
        <Input value={item.title} placeholder='Tiêu đề' onChange={e => {
            if (!allowEdit) return;
            setColumns(columns => {
                item.title = e.target.value;
                columns[index] = { ...item };
                return [...columns]
            })
        }} style={{ width: "75%" }} />
        <InputNumber min={0}
            max={100}
            value={item.rate}
            formatter={value => `${value}%`}
            placeholder='Tỷ lệ'
            onChange={v => {
                if (!allowEdit) return;
                setColumns(columns => {
                    item.rate = Math.floor(v);
                    columns[index] = { ...item };
                    return [...columns]
                })
            }}
            style={{ width: "25%" }}
        />
        <div style={{ position: "relative" }}>
            <Select
                value={item.test_release}
                style={{ width: "100%", textAlign: "left" }}
                options={tests}
                onChange={v => {
                    if (!allowEdit) return;
                    setColumns(columns => {
                        item.test_release = v;
                        columns[index] = { ...item };
                        return [...columns]
                    })
                }}
            />
            {allowEdit && <div style={{ position: "absolute", right: 0, bottom: 0 }} >
                {item.test_release === null && original && <Button type='primary' onClick={() => setUpFileRate(item)}>
                    <FileAddFilled />
                </Button>}
                <Button danger type='primary' onClick={() => {
                    setColumns(columns => columns.filter((_, i) => i !== index))
                }}>
                    <MinusCircleFilled />
                </Button>
            </div>}
        </div>
    </div>
}

export default function ScoreHandle({ course, selectedTerm, setPageMask }) {
    const [tests, setTests] = useState([]);
    const [columns, setColumns] = useState([]);
    const [students, setStudents] = useState([]);
    const [countStudents, setCountStudents] = useState(0);
    const [originalColumns, setOriginalColumns] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [editScores, setEditScores] = useState([]);
    const [upFileRate, setUpFileRate] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!selectedTerm) return;
        async function getData() {
            try {
                const response = await API.get("/tests/", {
                    params: {
                        page_size: 1000,
                        course: course.id,
                        releases__start_time__gte: selectedTerm.start_time.format("YYYY-MM-DD"),
                        releases__end_time__lte: selectedTerm.end_time.format("YYYY-MM-DD"),
                    }
                });
                const results = [];
                for (const t of response.data.results) {
                    for (const r of t.releases) {
                        r.start_time = dayjs(r.start_time);
                        r.end_time = dayjs(r.end_time);
                        const start_time = r.start_time.format("DD/MM/YYYY HH:mm:ss");
                        const end_time = r.end_time.format("DD/MM/YYYY HH:mm:ss");
                        results.push({
                            value: r.id,
                            label: `${t.title} - (${start_time} - ${end_time})`
                        })
                    }
                }
                setTests([{ value: null, label: "Điểm nhập tay" }, ...results]);
            } catch (e) {
                console.log(e);
            } finally {

            }
        }
        getData();
    }, [selectedTerm, course.id])

    const getData = useCallback(async (refresh = false) => {
        if (!selectedTerm) return;
        setPageMask(true);
        if (refresh) {
            setStudents([]);
        }
        try {
            const response = await API.post("/rate-course-score/", {
                course_id: course.id,
                term_id: selectedTerm.id,
                page: page, page_size: pageSize,
                name: search
            });
            const columns = response.data.columns.map(item => ({
                ...item,
                test_release__end_time: item.test_release__end_time && dayjs(item.test_release__end_time)
            }))
            setColumns(columns);
            setOriginalColumns(columns);
            setStudents(response.data.students);
            setCountStudents(response.data.count);
            setEditScores([]);
        } catch (e) {
            console.log(e);
        } finally {
            setPageMask(false);
        }
    }, [setPageMask, course, selectedTerm, page, pageSize, search])

    useEffect(() => {
        getData();
        // eslint-disable-next-line
    }, [getData])

    const allowEdit = useMemo(() => selectedTerm?.end_time.isAfter(NOW), [selectedTerm])

    const saveRate = useCallback(async () => {
        setPageMask(true);
        try {
            await API.post("/rate-course-score/", {
                course_id: course.id,
                term_id: selectedTerm.id,
                score_rates: columns,
            });
            await getData();
            toastr.success("Cập nhật tỷ lệ điểm thành công!")
        } catch (e) {
            console.log(e);
        } finally {
            setPageMask(false);
        }
        // eslint-disable-next-line
    }, [columns, course?.id, selectedTerm?.id, page, pageSize, search, getData])

    const saveScore = useCallback(async (editScores) => {
        setPageMask(true);
        try {
            await API.post("/submit-custom-score/", {
                scores: editScores
            });
            setEditScores([]);
            toastr.success("Cập nhật điểm thành công!")
        } catch (e) {
            console.log(e);
        } finally {
            setPageMask(false);
        }
    }, [setPageMask])

    const newColumn = useCallback(() => {
        const uni = new Date().getTime();
        return {
            id: -uni,
            test_release: null,
            title: "",
            rate: 0,
        }
    }, [])

    const nameColumn = useMemo(() => ({
        title: "Sinh viên",
        dataIndex: 'full_name',
        key: 'full_name',
        render: (v, item, index) => <>{item.last_name} {item.first_name}</>,
    }), [])

    const addColumn = useMemo(() => ({
        title: <Button type="primary" onClick={() => {
            setColumns(columns => [...columns, newColumn()])
        }} ><PlusCircleFilled /></Button>,
        dataIndex: 'none_value',
        key: 'none_value',
    }), [newColumn])

    const resultColumn = useMemo(() => ({
        title: "Kết quả",
        dataIndex: 'result',
        key: 'result',
        render: (v, item) =>
            formatScore(columns.reduce((sum, c) => sum + ((item[`score_${c.id}`] || 0) * (c.rate / 100)), 0)),
    }), [columns])

    const passedColumn = useMemo(() => ({
        title: "Qua môn",
        dataIndex: 'result',
        key: 'result',
        render: (v, item) =>
            columns.reduce((sum, c) => sum + ((item[`score_${c.id}`] || 0) * (c.rate / 100)), 0) >= 500
                ? <CheckCircleFilled style={{ color: "green" }} /> : <CloseCircleFilled style={{ color: "red" }} />,
    }), [columns])

    const handleDownload = useCallback(() => {
        setDownloading(true);
        API.post("/rate-course-score/", {
            term_id: selectedTerm.id,
            name: search,
            export: true,
            course_id: course.id,
        })
        .then(res => {
            const path = correct_media(res.data);
            downloadFile(path);
        })
        .catch(error => {

        })
        .finally(() => {
            setDownloading(false);
        })
    },[search, selectedTerm.id, course.id])

    const sumRate = columns.reduce((sum, item) => sum + item.rate, 0);

    return <div style={{ position: "relative" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: sumRate > 100 ? colorSet["low"] : null }}>
                <b>Tổng:</b> {sumRate}%
                {sumRate > 100 && <> - Tỷ lệ đang lớn hơn 100</>}
            </span>
            {allowEdit ? <Tooltip color={colorSet["low"]} title={sumRate > 100 ? "Tỷ lệ đang lớn hơn 100, không thể lưu" : ""}><Button type='primary' onClick={saveRate} disabled={originalColumns === columns || sumRate > 100}>
                Lưu tỷ lệ <SaveFilled />
            </Button></Tooltip> : <span style={{ color: "blue" }}>Kỳ đã kết thúc, không thể thay đổi cơ cấu điểm số!</span>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, marginBottom: 3 }}>
            <span>
                <Input.Search
                    placeholder='Tìm sinh viên'
                    onSearch={setSearch}
                    style={{ width: 400, marginRight: 3 }}
                    disabled={originalColumns !== columns}
                    allowClear
                />
                <Button type='primary' loading={downloading} disabled={downloading} onClick={handleDownload}>
                    Tải file <SaveOutlined />
                </Button>
            </span>
            <span>
                {editScores.length > 0 && allowEdit && <Button onClick={() => saveScore(editScores)} type='primary' disabled={originalColumns !== columns}>
                    Lưu điểm <SaveFilled />
                </Button>}
            </span>
        </div>
        <div style={{ overflowX: "auto" }}>
            <Table rowKey="id" columns={[nameColumn, ...(columns.map((item, index) => ({
                title: <ColumnTitle item={item} index={index} setColumns={setColumns} original={originalColumns === columns} tests={tests} allowEdit={allowEdit} setUpFileRate={setUpFileRate} />,
                key: `score_${item.id}`,
                dataIndex: `score_${item.id}`,
                render: (v, record, index) => item.test_release === null ?
                    (allowEdit ? <InputNumber disabled={item.id < 0} min={0} max={10} placeholder='Chưa nhập điểm' defaultValue={v && v/100} onChange={v => {
                        const scoreIndex = editScores.findIndex(
                            i => i.student === record.id && i.rate === item.id
                        )
                        if (scoreIndex !== -1) {
                            editScores[scoreIndex] = { student: record.id, rate: item.id, score: v * 100 };
                        } else {
                            editScores.push({ student: record.id, rate: item.id, score: v * 100 });
                        }
                        setEditScores([...editScores]);
                    }} /> : formatScore(v))
                    : (v !== undefined && formatScore(v, item.test_release__end_time)),
            }))), ...(allowEdit ? [addColumn] : []), resultColumn, ...(allowEdit ? [] : [passedColumn])]} dataSource={students}
                pagination={{
                    total: countStudents,
                    current: page,
                    pageSize: pageSize,
                    onChange: (page, pageSize) => {
                        setPage(page); setPageSize(pageSize);
                    },
                    disabled: originalColumns !== columns,
                    showSizeChanger: true,
                    position: ["bottomLeft"],
                }}
            />
        </div>
        <SubmitFile upFileRate={upFileRate} setUpFileRate={setUpFileRate} getData={getData}/>
    </div>
}
